import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import * as maptilersdk from "@maptiler/sdk";

import { limit } from "src/components/map/map";
import { activateAllActions } from "../utils/actions";
import maplibregl, { LngLatLike } from "maplibre-gl";
import { exaggeration } from "src/components/map/map";
import { SharedStateEvent, setSharedStateListener } from "../utils/shared-state";
import { TRACK_EXAGGERATION_MOD } from "./3DPolyline";
import { mean } from "../utils/metrics";



const WORLD_SIZE = 1024000; //TILE_SIZE * 2000
const MERCATOR_A = 6378137.0; // 900913 projection property. (Deprecated) Replaced by EARTH_RADIUS
const FOV_ORTHO = (0.1 / 180) * Math.PI; //Mapbox doesn't accept 0 as FOV
const FOV = Math.atan(3 / 4); //from Mapbox https://github.com/mapbox/mapbox-gl-js/blob/main/src/geo/transform.js#L93
const EARTH_RADIUS = 6371008.8; //from Mapbox  https://github.com/mapbox/mapbox-gl-js/blob/0063cbd10a97218fb6a0f64c99bf18609b918f4c/src/geo/lng_lat.js#L11
const EARTH_CIRCUMFERENCE_EQUATOR = 40075017; //from Mapbox https://github.com/mapbox/mapbox-gl-js/blob/0063cbd10a97218fb6a0f64c99bf18609b918f4c/src/geo/lng_lat.js#L117
const SMOOTHING_WINDOW = 1


const Constants = {
  WORLD_SIZE: WORLD_SIZE,
  PROJECTION_WORLD_SIZE: WORLD_SIZE / (EARTH_RADIUS * Math.PI * 2),
  MERCATOR_A: EARTH_RADIUS,
  DEG2RAD: Math.PI / 180,
  RAD2DEG: 180 / Math.PI,
  EARTH_RADIUS: EARTH_RADIUS,
  EARTH_CIRCUMFERENCE: 2 * Math.PI * EARTH_RADIUS, //40075000, // In meters
  EARTH_CIRCUMFERENCE_EQUATOR: EARTH_CIRCUMFERENCE_EQUATOR,
  FOV_ORTHO: FOV_ORTHO, // closest to 0
  FOV: FOV, // Math.atan(3/4) radians. If this value is changed, FOV_DEGREES must be calculated
  FOV_DEGREES: (FOV * 180) / Math.PI, // Math.atan(3/4) in degrees
  TILE_SIZE: 512,
};

function getZoomScaleFactor(zoomLevel: number) {
  // Coefficients for the exponential decay function
  const a = 9229.18;
  const b = 0.7501;
  const c = -144.26;

  if (zoomLevel <= 10) {
      return 500;
  } else if (zoomLevel > 10 && zoomLevel <= 14) {
      // Calculate the value of the exponential decay function
      return a * Math.pow(b, zoomLevel) + c;
  } else {
      return 10;
  }
}

// TODO: Add to AwesomeDB and load from there
const sceneActions = {
  Seal_Animation: {
    actions: {
      idleActionIdx: 9,
      glideActionIdx: 28,
      swimActionIdx: 15,
    },
  },
  "Seal._2": {
    actions: {
      idleActionIdx: 0,
      glideActionIdx: 0,
      swimActionIdx: 0,
    },
  },
  'Dolphin': {
    actions: {
      swimActionIdx: 0,
    },
  },
};

// const offset = -0.000002;
const offset = 0;
const offset_multiplier = 0.8

function getModelTransform(track: THREE.CatmullRomCurve3, idx, map, length) {
  let _idx = idx;
  const modelOrigin = map.getCenter(); // just an example
  const modelElevation = 0;
  const modelOriginMercator = maplibregl.MercatorCoordinate.fromLngLat(
    modelOrigin,
    0
  );

  const terrainCenterElevation = map.transform.elevation + offset;
  const deltaMetersZ = modelElevation - terrainCenterElevation;
  const mercatorPerMeter = modelOriginMercator.meterInMercatorCoordinateUnits();
  const deltaMercatorZ = deltaMetersZ * mercatorPerMeter;

  const trackPoint = worldToLngLats(track.getPoint(_idx / length));
  var modelPosition = [trackPoint.Long, trackPoint.Lat] as LngLatLike;
  var modelAltitude = -trackPoint.Depth * exaggeration * TRACK_EXAGGERATION_MOD * offset_multiplier;
  var modelRotate = [-Math.PI/2, 0, Math.PI];

  var modelAsMercatorCoordinate = maptilersdk.MercatorCoordinate.fromLngLat(
    modelPosition,
    modelAltitude
  );

  // transformation parameters to position, rotate and scale the 3D model onto the map
  const zoomScaleFactor = getZoomScaleFactor(map.getZoom());
  var modelTransform = {
    translateX: modelAsMercatorCoordinate.x,
    translateY: modelAsMercatorCoordinate.y,
    translateZ: (modelAsMercatorCoordinate.z + deltaMercatorZ + offset),
    rotateX: modelRotate[0],
    rotateY: modelRotate[1],
    rotateZ: modelRotate[2],
    /* Since our 3D model is in real world meters, a scale transform needs to be
     * applied since the CustomLayerInterface expects units in MercatorCoordinates.
     */
    scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits() * zoomScaleFactor
  };

  return {
    modelTransform,
    center: modelOrigin,
  };
}

const projectedUnitsPerMeter = (latitude) => {
  return Math.abs(
    Constants.WORLD_SIZE /
    Math.cos(Constants.DEG2RAD * latitude) /
    Constants.EARTH_CIRCUMFERENCE
  );
};

const projectToWorld = (coords) => {
  // Spherical mercator forward projection, re-scaling to WORLD_SIZE

  var projected = [
    -Constants.MERCATOR_A *
    Constants.DEG2RAD *
    coords[0] *
    Constants.PROJECTION_WORLD_SIZE,
    -Constants.MERCATOR_A *
    Math.log(Math.tan(Math.PI * 0.25 + 0.5 * Constants.DEG2RAD * coords[1])) *
    Constants.PROJECTION_WORLD_SIZE,
  ];

  //z dimension, defaulting to 0 if not provided
  if (!coords[2]) projected.push(0);
  else {
    var pixelsPerMeter = projectedUnitsPerMeter(coords[1]);
    projected.push(coords[2] * pixelsPerMeter);
  }

  return new THREE.Vector3(projected[0], projected[1], projected[2]);
};

const lnglatsToWorld = (track) => {
  const coords = track.map((pt) => [pt.Long, pt.Lat, -pt.Depth * exaggeration * TRACK_EXAGGERATION_MOD]);
  var vector3 = coords.map(function (pt) {
    var p = projectToWorld(pt);
    var v3 = new THREE.Vector3(p.x, p.y, p.z);
    return v3;
  });

  return vector3;
};

const worldToLngLats = (v3) => {
  // const coords = vector3.map((v3) => {
  // Inverse of the forward x, y projection to get longitude and latitude
  const lon =
    (v3.x / (-Constants.MERCATOR_A * Constants.PROJECTION_WORLD_SIZE)) *
    (1 / Constants.DEG2RAD);
  const lat =
    (2 *
      Math.atan(
        Math.exp(
          -v3.y / (Constants.MERCATOR_A * Constants.PROJECTION_WORLD_SIZE)
        )
      ) -
      Math.PI / 2) *
    (1 / Constants.DEG2RAD);

  // Inverse of the z dimension scaling to get depth
  var pixelsPerMeter = projectedUnitsPerMeter(lat); // Assuming this function returns the scale factor used in projectToWorld
  var depth = v3.z / pixelsPerMeter;

  return { Lat: lat, Long: lon, Depth: -depth / exaggeration / TRACK_EXAGGERATION_MOD }; // Assuming depth was negated during projection
};

// configuration of the custom layer for a 3D model per the CustomLayerInterface
function customLayer(track, sceneName, map, playback) {

  function animate() {
    if (layer.modelReady) {
      if (layer.playback.playing) {
        const step = layer.clock.getDelta() * layer.playback.speed * layer.stroke_speed
        layer.mixer.update(step);
      }
    }
  }

  const l = track.length

  const layer = {
    id: "3d-model",
    type: "custom",
    renderingMode: "3d",
    modelReady: false,
    map: map,
    idx: 0,
    mixer: null,
    clock: null,
    length: l,
    playback: playback,
    stroke_speed: 1,
    // track: track,
    onAdd: function (map, gl) {
      this.camera = new THREE.Camera();
      this.scene = new THREE.Scene();
      this.track = new THREE.CatmullRomCurve3(
        lnglatsToWorld(track)
      );
      this.clock = new THREE.Clock()
      // create two three.js lights to illuminate the model
      var directionalLight = new THREE.DirectionalLight(0xffffff);
      directionalLight.position.set(0, -70, 100).normalize();
      this.scene.add(directionalLight);

      var directionalLight2 = new THREE.DirectionalLight(0xffffff);
      directionalLight2.position.set(0, 70, 100).normalize();
      this.scene.add(directionalLight2);

      // use the three.js GLTF loader to add the 3D model to the three.js scene
      var loader = new GLTFLoader();
      loader.load(
        `./gltf/${sceneName}.glb`,
        function (gltf) {
          const model = gltf.scene;
          this.model = model;
          const mixer = new THREE.AnimationMixer(model);
          this.mixer = mixer
          const animations = gltf.animations;

          const swimAction = mixer.clipAction(
            animations[sceneActions[sceneName].actions.swimActionIdx]
          );

          swimAction.play();
          this.scene.add(gltf.scene);
          this.modelReady = true
          const axesHelper = new THREE.AxesHelper(10);
          this.scene.add(axesHelper);
          this.axesHelper = axesHelper
        }.bind(this)
      );
      this.map = map;

      // use the map canvas for three.js
      this.renderer = new THREE.WebGLRenderer({
        canvas: map.getCanvas(),
        context: gl,
        antialias: true,
      });

      setSharedStateListener(SharedStateEvent.PLAYBACK_UPDATE, (state) => {
        if (state.playback) {
          this.playback = state.playback
        }
      })

      animate()

      this.renderer.autoClear = false;
    },
    followPath: function (matrix) {
      const { modelTransform, center } = getModelTransform(
        this.track,
        window.sharedState.idx,
        this.map,
        this.length
      );

      var rotationX = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(1, 0, 0),
        modelTransform.rotateX
      );
      var rotationY = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(0, 1, 0),
        modelTransform.rotateY
      );
      var rotationZ = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(0, 0, 1),
        modelTransform.rotateZ
      );
      
      var m = new THREE.Matrix4().fromArray(matrix);
      var l = new THREE.Matrix4()
        .makeTranslation(
          modelTransform.translateX,
          modelTransform.translateY,
          modelTransform.translateZ
        )
        .scale(
          new THREE.Vector3(
            modelTransform.scale,
            -modelTransform.scale,
            modelTransform.scale
          )
        )

      this.camera.projectionMatrix = m.multiply(l).multiply(rotationX).multiply(rotationY).multiply(rotationZ);
      try {
        let _idx = window.sharedState.idx
        const track_window = track.slice(_idx - SMOOTHING_WINDOW, _idx + SMOOTHING_WINDOW)
        let roll = Number(
          mean(track_window, "roll")
        );    // Roll around Z-axis
        let pitch = -Number(
          mean(track_window, "pitch")
        );  // Pitch around X-axis
        let heading = Number(
          mean(track_window, "heading")
        );

        var rotationX = new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(1, 0, 0),
          pitch
        );
        var rotationY = new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(0, 1, 0),
          -heading
        );
        var rotationZ = new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(0, 0, 1),
          roll
        );

        // this.camera.projectionMatrix = this.camera.projectionMatrix
        //   .multiply(rotationZ)
        //   .multiply(rotationX)
        //   .multiply(rotationY)
        const quaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, -heading, roll, 'YXZ'));
        this.model.quaternion.copy(quaternion);
        this.axesHelper.quaternion.copy(quaternion)
        this.stroke_speed = Number(mean(track_window, "Stroke_Rate") / 50)
        
        
      } catch (error) {
        // Expected output: ReferenceError: nonExistentFunction is not defined
        // (Note: the exact output may be browser-dependent)
      }

      
      this.renderer.state.reset();
      animate();
      this.renderer.render(this.scene, this.camera);
      this.map.triggerRepaint();
      this.renderer.resetState();
    },

    render: function (gl, matrix) {
      this.followPath(matrix);
    },
  };

  return layer;
}

export default customLayer;
